import { GroupsApi } from "api/controllers/GroupsApi";
import { EventApi } from "api/controllers/EventApi";
import { requestErrorHandler } from "api/utils/api";
import { CarouselDataType } from "components/partial/carousels-array/carousel-data-provider";
import { defaultCarouselRecordsParams, getParams, getUpdatedParams } from "helpers/carousel.helper";
import { CourseFilter, ThreadFilter, UserFilter, getInitParams, fistLastNameSort } from "helpers/filter.helper";
import { GroupAssignmentType } from "helpers/groups.helper";
import { initialCarouselPagingParams } from "helpers/params.helper";
import { omit } from "lodash";
import { makeAutoObservable, runInAction } from "mobx";
import { IGetRecordsResponse } from "models/dto/ZoomiLxp/Models/Common/IGetRecordsResponse";
import { IFileModel } from "models/dto/ZoomiLxp/Models/Common/IFileModel";
import { ISaveEventGroupHistoryRequestModel } from "models/dto/ZoomiLxp/Models/EventModels/ISaveEventGroupHistoryRequestModel";
import { IGroupModel } from "models/dto/ZoomiLxp/Models/GroupModels/IGroupModel";
import { ICreateGroupModel } from "models/dto/ZoomiLxp/Models/GroupModels/ICreateGroupModel";
import { IGroupHomePageModel } from "models/dto/ZoomiLxp/Models/GroupModels/IGroupHomePageModel";
import { IPreviewGroupModel } from "models/dto/ZoomiLxp/Models/GroupModels/IPreviewGroupModel";
import { IUpdateGroupModel } from "models/dto/ZoomiLxp/Models/GroupModels/IUpdateGroupModel";
import { IGroupAssignment } from "models/dto/ZoomiLxp/Models/Profile/IGroupAssignment";
import { IPagingParams } from "models/dto/ZoomiLxp/Models/Query/IPagingParams";
import { IQueryParams } from "models/dto/ZoomiLxp/Models/Query/IQueryParams";
import { PermissionConstantsCreateOrUpdate } from "models/dto/ZoomiLxp/Utilities/Constants/PermissionConstantsCreateOrUpdate";
import { PermissionConstantsDelete } from "models/dto/ZoomiLxp/Utilities/Constants/PermissionConstantsDelete";
import { PermissionConstantsUpdate } from "models/dto/ZoomiLxp/Utilities/Constants/PermissionConstantsUpdate";
import { GroupAssignmentTypes } from "models/dto/ZoomiLxp/Utilities/Enumerations/GroupAssignmentTypes";
import { GroupThresholdMode } from "models/dto/ZoomiLxp/Utilities/Enumerations/GroupThresholdMode";
import { AppStore } from "store";

const initGroupData = {
	id: 0,
	name: "",
	displayName: "",
	description: "",
	price: 0,
	thumbnail: {} as IFileModel,
	autoAssignCourses: false,
	discoverableForLearners: false,
	enableExamThreshold: false,
	examThresholdMode: GroupThresholdMode.PerCourse,
	enableZ1Threshold: false,
	z1ThresholdMode: GroupThresholdMode.PerCourse,
	enableEngagementThreshold: false,
	engagementThresholdMode: GroupThresholdMode.PerCourse,
	enableProgressThreshold: false,
	enableCertification: false,
	courses: [],
};

const initGroupHomeData: IGroupHomePageModel = {
	id: 0,
	displayName: "",
	description: "",
	price: 0,
	thumbnail: {} as IFileModel,
	courseCount: 0,
	contentCount: 0,
	durationMinutes: 0,
	rating: 0,
	ratingCount: 0,
	likesCount: 0,
	viewCount: 0,
	userRating: 0,
	isLiked: false,
	isBookmarked: false,
	isMember: false,
	memberCount: 0,
	progressPercent: 0,
	isCertificationGroup: false,
	certificationSummary: {
		isComplete: false,
		overallProgressPercent: 0,
		requirements: [],
	},
	courses: [],
};

type GroupHomeMemberCountSource = IGroupHomePageModel & {
	membersCount?: number;
};

const normalizeGroupHomeData = (group: IGroupHomePageModel): IGroupHomePageModel => {
	const memberCountSource = group as GroupHomeMemberCountSource;

	return {
		...group,
		memberCount: memberCountSource.memberCount ?? memberCountSource.membersCount ?? 0,
	};
};

export class GroupStore {
	private rootStore;
	private _groupData: IGroupModel = initGroupData;
	private _groupHomeData: IGroupHomePageModel = initGroupHomeData;
	private _isLoading: boolean = false;
	private _groupsSearchParams: IQueryParams = getInitParams();
	private _addedGroupsAssignmentData: GroupAssignmentType[] = [];
	private _removedGroupsAssignmentData: GroupAssignmentType[] = [];
	private _groupsAssignmentRequestData: IGroupAssignment[] = [];
	public allowedPrefixes: string[] = ["visgrp__", "grp__", "licensegrp__"];
	myGroups: IPreviewGroupModel[] = [];
	trendingGroups: IPreviewGroupModel[] = [];
	promoGroups: IPreviewGroupModel[] = [];
	private _loadingGroup: {
		[key: string]: boolean;
	} = {
		myGroups: false,
		trendingGroups: false,
		promoGroups: false,
	};
	private _groupsCarouselsPagingParams: {
		[key: string]: IGetRecordsResponse<CarouselDataType>;
	} = {
		myGroups: defaultCarouselRecordsParams,
		trendingGroups: defaultCarouselRecordsParams,
		promoGroups: defaultCarouselRecordsParams,
	};

	constructor(rootStore: AppStore) {
		this.rootStore = rootStore;
		makeAutoObservable(this);
	}

	initCourseParams = (): IQueryParams => {
		const isAllCoursesAllowed = this.rootStore.usersStore.checkAllUserPermissions([
			PermissionConstantsCreateOrUpdate.CoursesWithAnyCreator,
			PermissionConstantsDelete.GroupCmsManagement,
		]);
		const filterCriteria = !this.groupData.tagId
			? [CourseFilter.emptyResult]
			: isAllCoursesAllowed
			? [CourseFilter.tagId(this.groupData.tagId)]
			: [
					CourseFilter.tagId(this.groupData.tagId),
					CourseFilter.creatorId(this.rootStore.usersStore.currentUserInfo!.id),
			  ];

		return {
			...getInitParams(),
			filterCriteria,
		};
	};

	initThreadParams = (): IQueryParams => {
		const isAllThreadsAllowed = this.rootStore.usersStore.checkAllUserPermissions([
			PermissionConstantsDelete.GroupCmsManagement,
			PermissionConstantsUpdate.AllThreads,
		]);
		const filterCriteria = !this.groupData.tagId
			? [ThreadFilter.emptyResult]
			: isAllThreadsAllowed
			? [ThreadFilter.tagId(this.groupData.tagId), ThreadFilter.courseItemId]
			: [
					ThreadFilter.tagId(this.groupData.tagId),
					ThreadFilter.courseItemId,
					ThreadFilter.accountCreatedId(this.rootStore.usersStore.currentUserInfo!.id),
			  ];

		return {
			...getInitParams(),
			filterCriteria,
		};
	};

	initUsersParams = (): IQueryParams => ({
		...getInitParams(),
		filterCriteria: [UserFilter.groupsId(this.groupData.id)],
		sortCriteria: fistLastNameSort,
	});

	hasAllowedPrefixes = (name: string) => {
		let hasAllowedPrefix = false;
		this.allowedPrefixes.forEach((prefix) => {
			if (name.startsWith(prefix) && name.length > prefix.length) {
				hasAllowedPrefix = true;
				return;
			}
		});
		return hasAllowedPrefix;
	};

	updateCourseParams() {
		this.rootStore.searchStore.params = this.initCourseParams();
	}

	updateThreadsParams() {
		this.rootStore.searchStore.params = this.initThreadParams();
	}

	updateUsersParams() {
		this.rootStore.searchStore.params = this.initUsersParams();
	}

	async getGroupData(id: number) {
		const response = await GroupsApi.getGroup(id);
		this.groupData = response.data.data;
		return response.data.data;
	}

	async getGroupHomeData(id: number, trackView = true) {
		const response = await GroupsApi.getGroupHome(id, trackView);
		const groupHomeData = response.data.data ?? (response.data as unknown as IGroupHomePageModel);

		if (!groupHomeData?.id) {
			throw new Error("No Group Data");
		}

		const normalizedGroupHomeData = normalizeGroupHomeData(groupHomeData);
		this.groupHomeData = normalizedGroupHomeData;
		return normalizedGroupHomeData;
	}

	async joinGroup(id: number) {
		await GroupsApi.joinGroup(id);
		return this.getGroupHomeData(id, false);
	}

	async sendGroupEvent(data: ISaveEventGroupHistoryRequestModel) {
		await EventApi.sendGroupEvent(data);
	}

	async updateGroup(id: number, data: IUpdateGroupModel) {
		this.isLoading = true;
		try {
			const response = await GroupsApi.updateGroup(id, data);
			this.groupData = response.data.data;
			return response.data.data;
		} finally {
			this.isLoading = false;
		}
	}

	async addGroup(data: ICreateGroupModel) {
		this.isLoading = true;
		try {
			const response = await GroupsApi.addGroup(data);
			this.groupData = response.data.data;
			return response.data.data;
		} finally {
			this.isLoading = false;
		}
	}

	async deleteGroup(id: number) {
		await GroupsApi.deleteGroup(id);
	}

	async deleteGroupFromCourse(courseId: number) {
		await GroupsApi.deleteGroupFromCourse(this.groupData.id, courseId);
	}

	async setGroupCourses(courseIds: number[]) {
		await GroupsApi.setGroupCourses(this.groupData.id, { courseIds });
	}

	async previewGroupMembers(groupId: number, data: Parameters<typeof GroupsApi.previewGroupMembers>[1]) {
		return GroupsApi.previewGroupMembers(groupId, data);
	}

	async commitGroupMembers(groupId: number, data: Parameters<typeof GroupsApi.commitGroupMembers>[1]) {
		return GroupsApi.commitGroupMembers(groupId, data);
	}

	async deleteGroupFromThread(threadId: number) {
		await GroupsApi.deleteGroupFromThread(this.groupData.id, threadId);
	}

	async deleteGroupFromUser(userId: number) {
		await GroupsApi.deleteGroupFromUser(this.groupData.id, userId);
	}

	async getCMSGroups() {
		this.isLoading = true;
		try {
			this.groupsSearchParams = this.rootStore.searchStore.params;
			const response = await GroupsApi.getGroupsStatistic(this.groupsSearchParams);
			return response.data.data;
		} catch (err) {
			requestErrorHandler(err);
		} finally {
			this.isLoading = false;
		}
	}

	async getMyGroups(data?: IPagingParams) {
		runInAction(() => {
			this._loadingGroup = { ...this.loadingGroup, myGroups: true };
		});
		try {
			const myGroups = await GroupsApi.getMyGroups(data ?? initialCarouselPagingParams);
			const newParams = getParams(myGroups.data.data);
			const oldParams = this._groupsCarouselsPagingParams.myGroups;
			runInAction(() => {
				this.myGroups = myGroups.data.data.records;
				this._groupsCarouselsPagingParams = {
					...this._groupsCarouselsPagingParams,
					myGroups: getUpdatedParams(newParams, oldParams),
				};
			});
			return myGroups.data.data;
		} finally {
			runInAction(() => {
				this._loadingGroup = { ...this.loadingGroup, myGroups: false };
			});
		}
	}

	async getTrendingGroups(data?: IPagingParams) {
		runInAction(() => {
			this._loadingGroup = { ...this.loadingGroup, trendingGroups: true };
		});
		try {
			const trendingGroups = await GroupsApi.getTrendingGroups(data ?? initialCarouselPagingParams);
			const newParams = getParams(trendingGroups.data.data);
			const oldParams = this._groupsCarouselsPagingParams.trendingGroups;
			runInAction(() => {
				this.trendingGroups = trendingGroups.data.data.records;
				this._groupsCarouselsPagingParams = {
					...this._groupsCarouselsPagingParams,
					trendingGroups: getUpdatedParams(newParams, oldParams),
				};
			});
			return trendingGroups.data.data;
		} finally {
			runInAction(() => {
				this._loadingGroup = { ...this.loadingGroup, trendingGroups: false };
			});
		}
	}

	async getPromoGroups(data?: IPagingParams) {
		runInAction(() => {
			this._loadingGroup = { ...this.loadingGroup, promoGroups: true };
		});
		try {
			const promoGroups = await GroupsApi.getPromoGroups(data ?? initialCarouselPagingParams);
			const newParams = getParams(promoGroups.data.data);
			const oldParams = this._groupsCarouselsPagingParams.promoGroups;
			runInAction(() => {
				this.promoGroups = promoGroups.data.data.records;
				this._groupsCarouselsPagingParams = {
					...this._groupsCarouselsPagingParams,
					promoGroups: getUpdatedParams(newParams, oldParams),
				};
			});
			return promoGroups.data.data;
		} finally {
			runInAction(() => {
				this._loadingGroup = { ...this.loadingGroup, promoGroups: false };
			});
		}
	}

	clearHomeGroups() {
		runInAction(() => {
			this.myGroups = [];
			this.trendingGroups = [];
			this.promoGroups = [];
			this._groupsCarouselsPagingParams = {
				myGroups: defaultCarouselRecordsParams,
				trendingGroups: defaultCarouselRecordsParams,
				promoGroups: defaultCarouselRecordsParams,
			};
		});
	}

	addGroupToAssign(group: GroupAssignmentType) {
		const groupRequestData: IGroupAssignment = omit(group, "groupName");
		runInAction(() => {
			this.groupsAssignmentRequestData.push(groupRequestData);
		});
	}

	removeGroupToAssign(removedGroup: GroupAssignmentType) {
		runInAction(() => {
			this.groupsAssignmentRequestData = this.groupsAssignmentRequestData.filter(
				(group) => group.groupId !== removedGroup.groupId
			);
		});
	}

	get groupData() {
		return this._groupData;
	}

	set groupData(data: IGroupModel) {
		this._groupData = data;
	}

	get groupHomeData() {
		return this._groupHomeData;
	}

	set groupHomeData(data: IGroupHomePageModel) {
		this._groupHomeData = data;
	}

	get isLoading() {
		return this._isLoading;
	}

	set isLoading(data: boolean) {
		this._isLoading = data;
	}

	get loadingGroup() {
		return this._loadingGroup;
	}

	get groupsCarouselsPagingParams() {
		return this._groupsCarouselsPagingParams;
	}

	get groupsSearchParams() {
		return this._groupsSearchParams;
	}

	set groupsSearchParams(params: IQueryParams) {
		this._groupsSearchParams = params;
	}

	get addedGroupsAssignmentData() {
		return this._addedGroupsAssignmentData.filter((group) => group.type === GroupAssignmentTypes.Assign);
	}

	set addedGroupsAssignmentData(data: GroupAssignmentType[]) {
		this._addedGroupsAssignmentData = data;
	}

	get removedGroupsAssignmentData() {
		return this._removedGroupsAssignmentData.filter((group) => group.type === GroupAssignmentTypes.Unassign);
	}

	set removedGroupsAssignmentData(data: GroupAssignmentType[]) {
		this._removedGroupsAssignmentData = data;
	}

	get groupsAssignmentRequestData() {
		return this._groupsAssignmentRequestData;
	}

	set groupsAssignmentRequestData(data: IGroupAssignment[]) {
		this._groupsAssignmentRequestData = data;
	}

	clearGroupsAssignRequestData() {
		runInAction(() => {
			this.groupsAssignmentRequestData = [];
		});
	}

	clearGroupsAssignPopupData() {
		this.clearGroupsAssignRequestData();
		runInAction(() => {
			this.addedGroupsAssignmentData = [];
			this.removedGroupsAssignmentData = [];
		});
	}
}
